import type { CSSProperties, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '@/store/tenantStore';
import styles from './EasyApplyButton.module.css';

/**
 * EasyApplyButton — mirrors Flutter EasyApplyButton (custom_button.dart).
 * Label and icon come from tenant preference `easyApplyButtonConfig` ({ title, iconUrl, iconType }),
 * falling back to "Easy Apply" with no icon. Every job-card / job-details caller in Flutter
 * passes btnColor white with border and text in primaryColorDark (`--accent`), which is the
 * default style here.
 */

interface EasyApplyButtonConfig {
  title?: string;
  iconUrl?: string;
  /** 'svg' or an image type — both render through <img> on web */
  iconType?: string;
}

interface EasyApplyButtonProps {
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  /** Per-caller sizing (Flutter fontSize / height arguments) */
  style?: CSSProperties;
}

export function EasyApplyButton({ onClick, className, style }: EasyApplyButtonProps) {
  const { t } = useTranslation();
  const preferences = useTenantStore((s) => s.preferences);
  const config = (preferences as Record<string, unknown> | null)?.easyApplyButtonConfig as
    | EasyApplyButtonConfig
    | undefined;

  return (
    <button type="button" className={`${styles.btn} ${className ?? ''}`} onClick={onClick} style={style}>
      {config?.iconUrl && (
        <img src={config.iconUrl} alt="" width={16} height={16} className={styles.icon} />
      )}
      {/* Flutter shows the tenant title as configured (untranslated) */}
      {config?.title ?? t('Easy Apply')}
    </button>
  );
}
